import uuid from 'uuid';

import { getPersistedValue, setPersistedValue } from 'shared/utils/persisted-values';

const sessionKey = 'dutchie-session';

type Data = {
  id: string;
};

function encode(data: any): string {
  return Buffer.from(JSON.stringify(data)).toString('base64');
}

function decode(str: string): any {
  return JSON.parse(Buffer.from(str, 'base64').toString('utf8'));
}

function hasValidData(data: any): data is Data {
  try {
    return typeof data.id === 'string';
  } catch {
    return false;
  }
}

function generate(): Data {
  console.debug('session: generated');

  return {
    id: uuid.v4(),
  };
}

function load(): Data | null {
  try {
    const cached = getPersistedValue(sessionKey);

    if (!cached) {
      console.debug('session: cached session is missing');
      return null;
    }

    const data = decode(cached);

    if (!hasValidData(data)) {
      console.debug('session: cached session is invalid');
      return null;
    }

    return data;
  } catch (err) {
    console.debug('session: failed loading cached session');
    console.error(err);
    return null;
  }
}

// Query param the embed script uses to hand a stable session id into the iframe.
// See `DutchieEmbedder.appendEmbedSessionId` in apps/api/src/embedded/embed-class.
// The embedded menu runs in a cross-origin iframe whose storage is partitioned
// per parent site (Chrome/Firefox) or blocked (Safari ITP), so on its own the id
// regenerates on every reload and fragments one visit into many separately-billed
// LaunchDarkly Session Replay sessions. The parent frame persists the id in its
// own first-party storage and forwards it here so the session stays continuous.
const embedSessionParam = 'dutchie_sid';

// Remove the handoff param from the visible URL once we've read it, so it doesn't
// pollute analytics `page_url`, referrers, or shared links. The parent frame
// re-supplies it on the next full reload. Preserves every other query param, the
// path, and the hash.
function stripEmbedParamFromUrl(params: URLSearchParams): void {
  try {
    params.delete(embedSessionParam);
    const query = params.toString();
    const newUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', newUrl);
  } catch {
    // replaceState can throw in rare sandboxed contexts; leaving the param is
    // harmless beyond the analytics noise this strip avoids.
  }
}

function loadFromEmbedHandoff(): Data | null {
  try {
    if (typeof window === 'undefined') {
      return null;
    }

    const params = new URLSearchParams(window.location.search);
    const id = params.get(embedSessionParam);
    if (!id) {
      return null;
    }

    // Consume-once: strip the param whether or not we adopt it, so it never leaks
    // into analytics or shared URLs.
    stripEmbedParamFromUrl(params);

    // Only adopt an externally-supplied id inside an embedded (iframe) consumer
    // menu. A standalone page (window.parent === window) must not take an id from
    // the URL — that would be session-id fixation from a crafted/shared link. A
    // kiosk must not either: it resets per customer (kiosks/lock.tsx), and adopting
    // a persistent parent id would undo that reset and bleed one customer's session
    // into the next.
    const inIframe = window.parent !== window;
    const isKiosk = window.location.pathname.startsWith('/kiosks/');
    if (!inIframe || isKiosk) {
      return null;
    }

    // Basic sanity bound so we never adopt an absurd value from the URL.
    if (id.length > 0 && id.length <= 128) {
      console.debug('session: adopted embed handoff id');
      return { id };
    }
  } catch {
    console.debug('session: failed reading embed handoff id');
  }

  return null;
}

export class Session {
  private static instance?: Session;
  private static readonly listeners: Set<() => void> = new Set();
  readonly data: Data;
  readonly encoded: string;

  // True only when `data.id` was adopted from the embed handoff (embedded,
  // non-kiosk iframe). Consumers that must NOT stabilize on an inherited id —
  // kiosks (reset per customer) and standalone pages — see `false`. `reset()`
  // and any `forceGenerate` construction always yield `false`.
  readonly fromEmbedHandoff: boolean;

  // `forceGenerate` bypasses the embed handoff and the local cache to mint a
  // brand-new id. Used by `reset()` (e.g. kiosk new-customer), which must not
  // re-adopt an inherited id.
  private constructor(forceGenerate = false) {
    const handoff = forceGenerate ? null : loadFromEmbedHandoff();
    this.fromEmbedHandoff = handoff !== null;
    this.data = handoff ?? (forceGenerate ? generate() : load() ?? generate());
    this.encoded = encode(this.data);

    setPersistedValue(sessionKey, this.encoded);
  }

  static getInstance(): Session {
    Session.instance ??= new Session();

    return Session.instance;
  }

  static destroy(): void {
    setPersistedValue(sessionKey, null);
    Session.instance = undefined;
  }

  /**
   * Subscribe to session resets. Returns an unsubscribe function;
   * the signature matches React's `useEffect` cleanup contract:
   *
   *   useEffect(() => Session.onReset(handler), []);
   */
  static onReset(callback: () => void): () => void {
    Session.listeners.add(callback);
    return () => {
      Session.listeners.delete(callback);
    };
  }

  /**
   * Mint a fresh session id and notify `onReset` subscribers so they
   * can re-identify (e.g., the LD client) with the new id.
   */
  public reset(): void {
    console.debug('session: reset');
    Session.destroy();
    // Force a fresh id: reset() means "new session now", so it must not re-adopt
    // an embed-handoff id that may still be present in the URL.
    Session.instance = new Session(true);
    Session.listeners.forEach((listener) => listener());
  }

  public get header(): { 'x-dutchie-session': string } {
    return { 'x-dutchie-session': this.encoded };
  }
}

// The LaunchDarkly Session Replay recorder (highlight.run) mints its OWN
// `sessionSecureID`, separate from the `dutchie-session` id above, persisted in
// the iframe's localStorage. In the embedded cross-origin menu that storage is
// partitioned (Chrome/Firefox) or blocked (Safari/iOS ITP), so the recorder
// regenerates its id on nearly every reload and fragments one visit into many
// separately-billed Session Replay sessions — the same failure the handoff fixed
// for the LD user key. highlight.run derives a stable `sessionSecureID` from a
// `sessionKey` even when storage can't persist, so seeding the recorder with the
// handed-off id keeps it continuous.
//
// Return the key ONLY when the id came from the embed handoff. Otherwise return
// undefined so the recorder keeps its default per-storage/per-reset behavior:
//   - Kiosks reset per customer; a stable key would merge distinct customers'
//     recordings into one session.
//   - Standalone pages have working first-party storage and must not adopt an
//     external id (session-fixation), and gain nothing from a fixed key.
export function getSessionReplaySessionKey(session: Session): string | undefined {
  return session.fromEmbedHandoff ? session.data.id : undefined;
}
